import { ColDef, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { Dispatch, SetStateAction } from 'react';

import Checkbox from '~/shared/components/Checkbox';
import Tag from '~/shared/components/Tag';
import { Volume } from '~/shared/types/api';

import { statusToTagVariantMap } from '../common/helpers';
import { DatafySwitchVolume } from '../DatafySwitchProvider';
import { tableColumns } from '../common/tableColumns';

import { canBeDatafied } from './helpers';
import OptimisticSwitch from './OptimisticSwitch';
import VolumeIdCellRenderer from './VolumeIdCellRenderer';

export const getColumnDefs = ({
  onSelectAllRows,
  sortField,
  selectedVolumes,
  setSelectedVolumes,
  datafySwitchVolumes,
}: {
  onSelectAllRows: VoidFunction;
  sortField: { field: string; dir: 'desc' | 'asc' };
  selectedVolumes: string[];
  setSelectedVolumes: Dispatch<SetStateAction<string[]>>;
  datafySwitchVolumes: DatafySwitchVolume[];
}): ColDef<Volume>[] => [
  {
    headerComponent: ({ api }: IHeaderParams<Volume>) => {
      const allRows: Volume[] = [];
      api.forEachNode((node) => (node?.data ? allRows.push(node.data) : null));
      const selectableRows = allRows.filter(canBeDatafied);
      const displayingOptimisticValue = datafySwitchVolumes.some((v) =>
        selectableRows.find((r) => r.volumeId === v.volumeId)
      );

      return (
        <Checkbox
          onClick={onSelectAllRows}
          checked={
            selectedVolumes?.length > 0 && selectedVolumes?.length === selectableRows?.length
          }
          disabled={selectableRows?.length === 0 || displayingOptimisticValue}
          className="w-[16px] h-[16px] mb-[5px]"
        />
      );
    },
    colId: 'row-selector',
    cellRenderer: ({ data }: ICellRendererParams<Volume>) => {
      const displayingOptimisticValue = datafySwitchVolumes.some(
        (v) => v.volumeId === data?.volumeId
      );
      return (
        <Checkbox
          checked={selectedVolumes?.includes(data?.volumeId || '')}
          onChange={() =>
            setSelectedVolumes((prev) =>
              prev.includes(data?.volumeId || '')
                ? prev.filter((v) => v !== data?.volumeId || '')
                : [...prev, data?.volumeId || '']
            )
          }
          disabled={!canBeDatafied(data!) || displayingOptimisticValue}
        />
      );
    },
    initialWidth: 45,
    minWidth: 45,
    pinned: 'left',
    resizable: false,
    suppressMovable: true,
  },
  {
    headerName: 'Datafy it?',
    initialWidth: 87,
    minWidth: 87,
    sortable: false,
    cellRenderer: ({ data }: ICellRendererParams<Volume>) => (
      <OptimisticSwitch volume={data!} setSelectedVolumes={setSelectedVolumes} />
    ),
    pinned: 'left',
    resizable: false,
    suppressMovable: true,
  },

  ...tableColumns.map(({ key, title, getValue }): ColDef<Volume> => {
    const defaultColDef: ColDef<Volume> = {
      field: key,
      headerName: title,
      valueFormatter: getValue ? ({ data, value }) => (data ? getValue?.(data) : value) : undefined,
    };

    if (key === 'volumeName') {
      return { ...defaultColDef, initialWidth: 194, minWidth: 194 };
    }

    if (key === 'volumeId') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'volumeId' ? sortField?.dir : null,
        initialWidth: 194,
        minWidth: 194,
        cellRenderer: ({ data }: ICellRendererParams<Volume>) =>
          data?.volumeId ? <VolumeIdCellRenderer id={data.volumeId} /> : null,
      };
    }

    if (key === 'monitoring') {
      return {
        ...defaultColDef,
        initialWidth: 146,
        minWidth: 146,
        sort: sortField?.field === 'monitoring' ? sortField?.dir : null,
        cellRenderer: ({ value, data }: ICellRendererParams<Volume>) => {
          if (!data || !value) {
            return null;
          }
          const volumeMonitoring = value as Volume['monitoring'];

          const machineVolume = datafySwitchVolumes.find((v) => v.volumeId === data?.volumeId);

          const optimisticValue: Volume['monitoring'] =
            machineVolume?.state === 'datafying' && volumeMonitoring === 'Monitored'
              ? 'Migrating'
              : machineVolume?.state === 'datafying' && volumeMonitoring === 'Migrating'
                ? 'Monitored'
                : machineVolume?.state === 'undatafying' && volumeMonitoring === 'Managed'
                  ? 'Unmigrating'
                  : machineVolume?.state === 'undatafying' && volumeMonitoring === 'Migrating'
                    ? 'Monitored'
                    : value;

          return (
            <Tag
              text={optimisticValue}
              progress={machineVolume ? undefined : data?.statusProgress}
              variant={statusToTagVariantMap[optimisticValue]}
            />
          );
        },
      };
    }

    if (key === 'diskSize') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'diskSize' ? sortField?.dir : null,
        initialWidth: 78,
        minWidth: 78,
      };
    }

    if (key === 'diskUsed') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'diskUsed' ? sortField?.dir : null,
        minWidth: 122,
        width: 122,
      };
    }

    if (key === 'datafyDiskSize') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'datafyDiskSize' ? sortField?.dir : null,
        initialWidth: 94,
        minWidth: 94,
      };
    }

    if (key === 'datafyDiskUsed') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'datafyDiskUsed' ? sortField?.dir : null,
        minWidth: 122,
        width: 122,
      };
    }

    if (key === 'diskType') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'diskType' ? sortField?.dir : null,
        initialWidth: 80,
        minWidth: 80,
      };
    }

    if (key === 'iops') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'volumeIops' ? sortField?.dir : null,
        minWidth: 80,
        initialWidth: 80,
      };
    }

    if (key === 'throughput') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'volumeThroughput' ? sortField?.dir : null,
        initialWidth: 124,
        minWidth: 124,
      };
    }

    if (key === 'awsState') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'awsState' ? sortField?.dir : null,
        initialWidth: 124,
        minWidth: 124,
      };
    }

    if (key === 'awsStatus') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'awsStatus' ? sortField?.dir : null,
        initialWidth: 124,
        minWidth: 124,
      };
    }

    if (key === 'instanceId') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
        initialWidth: 204,
        minWidth: 204,
      };
    }

    if (key === 'availabilityZone') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'availabilityZone' ? sortField?.dir : null,
        initialWidth: 154,
        minWidth: 154,
      };
    }

    if (key === 'region') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'region' ? sortField?.dir : null,
        initialWidth: 124,
        minWidth: 124,
      };
    }

    if (key === 'clusterName') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'clusterName' ? sortField?.dir : null,
        initialWidth: 100,
        minWidth: 100,
      };
    }

    if (key === 'nodeGroupName') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'nodeGroupName' ? sortField?.dir : null,
        initialWidth: 390,
        minWidth: 390,
      };
    }

    if (key === 'osImage') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'osImage' ? sortField?.dir : null,
        initialWidth: 158,
        minWidth: 158,
      };
    }

    if (key === 'isBootDisk') {
      return {
        ...defaultColDef,
        sort: sortField?.field === 'isBootDisk' ? sortField?.dir : null,
        initialWidth: 130,
        minWidth: 130,
        valueFormatter: undefined,
        cellRenderer: ({ data, value }: ICellRendererParams<Volume>) =>
          data ? getValue?.(data) : value,
      };
    }

    return defaultColDef;
  }),
];
